import { useEffect, useState } from 'react';

import { TrackIDManager } from '@/manager';
import {
  ArrowDropDown,
  ArrowRight,
  FilterNone,
  VFlex,
  VStack,
  new__cn as cn,
} from '@/package';
import type { BoxWithIndex } from '@/type';

type InvalidListProps = {
  boxGroupList: BoxWithIndex[][];
  selectedBoxIdMap: Dictionary<boolean>;
  renderGroupTitle?(groupIndex: number): React.ReactElement;
  onLabelingBoxClick(boxID: BoxWithIndex['id'][]): void;
};

const InvalidList = ({
  boxGroupList,
  selectedBoxIdMap,
  renderGroupTitle,
  onLabelingBoxClick,
}: InvalidListProps) => {
  const [hiddenByGroupIndex, setHiddenByGroupIndex] = useState<
    Dictionary<boolean>
  >({});

  const setVisible = (index: number, hidden: boolean) => {
    setHiddenByGroupIndex(prev => ({ ...prev, [index]: hidden }));
  };

  const toggle = (index: number) =>
    setVisible(index, !hiddenByGroupIndex[index]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHiddenByGroupIndex({});
  }, [boxGroupList]);

  return (
    <VStack className="text-body-lg" gap={0.125}>
      {boxGroupList.map((boxList, index) => {
        const hidden = hiddenByGroupIndex[index];

        return (
          <VStack className="pr-1" gap={0.125} key={index}>
            <VFlex items="center" gap={0.25} onClick={() => toggle(index)}>
              <div role="button" className="text-primary-500">
                {hidden ? (
                  <ArrowRight className="size-4" />
                ) : (
                  <ArrowDropDown className="size-4" />
                )}
              </div>

              <VFlex className="flex-1 py-1" items="center" justify="between">
                <VFlex
                  className="cursor-pointer"
                  items="center"
                  gap={0.25}
                  onClick={e => {
                    e.stopPropagation();

                    onLabelingBoxClick(boxList.map(box => box.id));
                  }}
                >
                  {typeof renderGroupTitle === 'function' ? (
                    renderGroupTitle(index)
                  ) : (
                    <>
                      <div className="text-primary-500">
                        <FilterNone className="size-4" />
                      </div>
                      <div>Overlap {index + 1}</div>
                    </>
                  )}
                </VFlex>

                <span className="text-body-sm text-gray-400">
                  {boxList.length}
                </span>
              </VFlex>
            </VFlex>

            {!hidden && (
              <VStack gap={0.125}>
                {boxList.map(box =>
                  // NOTE: box.id 가 없는 exception 이 발생하여 예외 처리 진행함
                  box.id ? (
                    <VFlex
                      key={box.id}
                      role="button"
                      className={cn('py-0.5 pl-2.5', {
                        'bg-skyBlue-200': selectedBoxIdMap[box.id],
                      })}
                      onClick={() => onLabelingBoxClick([box.id])}
                    >
                      #&nbsp;
                      {typeof box.trackId === 'string' &&
                      TrackIDManager.isValidTrackID(box.trackId)
                        ? box.trackId
                        : box.index}
                    </VFlex>
                  ) : null,
                )}
              </VStack>
            )}
          </VStack>
        );
      })}
    </VStack>
  );
};

InvalidList.displayName = 'InvalidList';

export default InvalidList;
